/**
 * Bark & Style Box Wizard
 * Multi-step form for personalized dog subscription boxes
 */

"use strict";

// ========================================
// CONSTANTS & CONFIGURATION
// ========================================

// Wait for and validate WIZARD_CONFIG from Shopify
if (!window.WIZARD_CONFIG) {
  console.error('❌ CRITICAL: window.WIZARD_CONFIG not found! Wizard cannot function.');
  console.log('Make sure the section is added to a page and products are selected in theme customizer.');
}

// Load configuration from Shopify section (set by index.liquid)
const CONFIG = window.WIZARD_CONFIG || {
  totalSteps: 8,
  checkoutUrl: '/checkout',
  products: {}
};

// Validate that products are configured
const hasProducts = CONFIG.products && Object.values(CONFIG.products).some(p => p !== null);
if (!hasProducts) {
  console.warn('⚠️ WARNING: No products configured in theme customizer!');
  console.log('Please go to Theme Customizer → Add Section → Bark Style Wizard → Configure Products');
}

const CONSTANTS = {
  STORAGE_KEYS: {
    DATA: "barkWizardData",
    STEP: "barkWizardStep",
  },
  TOTAL_STEPS: CONFIG.totalSteps,
  BOX_TYPES: {
    SUBSCRIBE: "Subscribe",
    SINGLE: "Single",
  },
  TIERS: {
    TRIAL: "Trial Bark Box",
    BASIC: "Basic Bark Box",
    PREMIUM: "Premium Bark Box",
    LUX: "Lux Bark Box",
  },
  CHECKOUT_URL: CONFIG.checkoutUrl,
  CART_API_URL: "/cart/add.js",
};

// Helper function to build variant map from Shopify product object
const buildVariantMap = (product) => {
  if (!product || !product.variants) {
    console.warn('⚠️ Product or variants missing:', product);
    return {};
  }
  
  const variantMap = {};
  // Map Shopify variant names to wizard size keys
  const sizeMapping = {
    'Extra Small': 'XS',
    'Small': 'Small',
    'Medium': 'Medium',
    'Large': 'Large',
    'Extra Large (XL)': 'XL',
    'Extra Large': 'XL',
    'XL': 'XL',
    'XS': 'XS'
  };
  
  console.log(`Building variant map for product: ${product.title || 'Unknown'}`);
  console.log('Available variants:', product.variants);
  
  product.variants.forEach(variant => {
    // Try to match variant title or option1 to size
    const title = (variant.title || variant.option1 || "").trim();
    
    // Direct match first
    let matchedKey = null;
    for (const [variantName, sizeKey] of Object.entries(sizeMapping)) {
      if (title === variantName || title.toLowerCase() === variantName.toLowerCase()) {
        matchedKey = sizeKey;
        break;
      }
    }
    
    // If no direct match, try contains
    if (!matchedKey) {
      for (const [variantName, sizeKey] of Object.entries(sizeMapping)) {
        if (title.toLowerCase().includes(variantName.toLowerCase())) {
          matchedKey = sizeKey;
          break;
        }
      }
    }
    
    if (matchedKey && variant.id) {
      // Ensure variant ID is a number (remove 'gid://shopify/ProductVariant/' prefix if present)
      const variantId = typeof variant.id === 'string' && variant.id.includes('/')
        ? parseInt(variant.id.split('/').pop(), 10)
        : parseInt(variant.id, 10);
      
      variantMap[matchedKey] = variantId;
      console.log(`✅ Mapped "${title}" to size "${matchedKey}" with variant ID: ${variantId}`);
    } else {
      console.log(`❌ Could not match variant "${title}" to a size`);
    }
  });
  
  console.log('Final variant map:', variantMap);
  return variantMap;
};

// Helper function to get selling plan ID from product
const getSellingPlanId = (product) => {
  if (!product || !product.selling_plan_groups || product.selling_plan_groups.length === 0) {
    return null;
  }
  
  const firstGroup = product.selling_plan_groups[0];
  if (firstGroup.selling_plans && firstGroup.selling_plans.length > 0) {
    const planId = firstGroup.selling_plans[0].id;
    // Extract numeric ID from GraphQL ID if needed (gid://shopify/SellingPlan/123456)
    return typeof planId === 'string' && planId.includes('/')
      ? parseInt(planId.split('/').pop(), 10)
      : parseInt(planId, 10);
  }
  
  return null;
};

// Build dynamic product configuration from Shopify products
const PRODUCT_CONFIG = {
  variantIds: {
    subscribeTrialQuarterly: buildVariantMap(CONFIG.products.subscribeTrialQuarterly),
    subscribeTrialYearly: buildVariantMap(CONFIG.products.subscribeTrialYearly),
    subscribeBasicQuarterly: buildVariantMap(CONFIG.products.subscribeBasicQuarterly),
    subscribeBasicYearly: buildVariantMap(CONFIG.products.subscribeBasicYearly),
    subscribePremiumQuarterly: buildVariantMap(CONFIG.products.subscribePremiumQuarterly),
    subscribePremiumYearly: buildVariantMap(CONFIG.products.subscribePremiumYearly),
    subscribeLuxQuarterly: buildVariantMap(CONFIG.products.subscribeLuxQuarterly),
    subscribeLuxYearly: buildVariantMap(CONFIG.products.subscribeLuxYearly),
    singleBoxTrial: buildVariantMap(CONFIG.products.singleBoxTrial),
    singleBoxBasic: buildVariantMap(CONFIG.products.singleBoxBasic),
    singleBoxPremium: buildVariantMap(CONFIG.products.singleBoxPremium),
    singleBoxLux: buildVariantMap(CONFIG.products.singleBoxLux),
    luxAddon: CONFIG.products.luxAddon?.variants?.[0]?.id 
      ? (typeof CONFIG.products.luxAddon.variants[0].id === 'string' && CONFIG.products.luxAddon.variants[0].id.includes('/')
          ? parseInt(CONFIG.products.luxAddon.variants[0].id.split('/').pop(), 10)
          : parseInt(CONFIG.products.luxAddon.variants[0].id, 10))
      : null,
  },

  sellingPlans: {
    subscribeTrialQuarterly: getSellingPlanId(CONFIG.products.subscribeTrialQuarterly),
    subscribeTrialYearly: getSellingPlanId(CONFIG.products.subscribeTrialYearly),
    subscribeBasicQuarterly: getSellingPlanId(CONFIG.products.subscribeBasicQuarterly),
    subscribeBasicYearly: getSellingPlanId(CONFIG.products.subscribeBasicYearly),
    subscribePremiumQuarterly: getSellingPlanId(CONFIG.products.subscribePremiumQuarterly),
    subscribePremiumYearly: getSellingPlanId(CONFIG.products.subscribePremiumYearly),
    subscribeLuxQuarterly: getSellingPlanId(CONFIG.products.subscribeLuxQuarterly),
    subscribeLuxYearly: getSellingPlanId(CONFIG.products.subscribeLuxYearly),
  },

  products: {
    subscribeTrialQuarterly: {
      name: CONFIG.products.subscribeTrialQuarterly?.title || "Trial Bark Box (Quarterly)",
      price: CONFIG.products.subscribeTrialQuarterly?.price ? parseFloat(CONFIG.products.subscribeTrialQuarterly.price) / 100 : 59.99,
      type: "subscription",
      tier: 0,
      frequency: "Quarterly",
    },
    subscribeTrialYearly: {
      name: CONFIG.products.subscribeTrialYearly?.title || "Trial Bark Box (Yearly)",
      price: CONFIG.products.subscribeTrialYearly?.price ? parseFloat(CONFIG.products.subscribeTrialYearly.price) / 100 : 199.99,
      type: "subscription",
      tier: 0,
      frequency: "Yearly",
    },
    subscribeBasicQuarterly: {
      name: CONFIG.products.subscribeBasicQuarterly?.title || "Basic Bark Box (Quarterly)",
      price: CONFIG.products.subscribeBasicQuarterly?.price ? parseFloat(CONFIG.products.subscribeBasicQuarterly.price) / 100 : 79.99,
      type: "subscription",
      tier: 1,
      frequency: "Quarterly",
    },
    subscribeBasicYearly: {
      name: CONFIG.products.subscribeBasicYearly?.title || "Basic Bark Box (Yearly)",
      price: CONFIG.products.subscribeBasicYearly?.price ? parseFloat(CONFIG.products.subscribeBasicYearly.price) / 100 : 279.99,
      type: "subscription",
      tier: 1,
      frequency: "Yearly",
    },
    subscribePremiumQuarterly: {
      name: CONFIG.products.subscribePremiumQuarterly?.title || "Premium Bark Box (Quarterly)",
      price: CONFIG.products.subscribePremiumQuarterly?.price ? parseFloat(CONFIG.products.subscribePremiumQuarterly.price) / 100 : 99.99,
      type: "subscription",
      tier: 2,
      frequency: "Quarterly",
    },
    subscribePremiumYearly: {
      name: CONFIG.products.subscribePremiumYearly?.title || "Premium Bark Box (Yearly)",
      price: CONFIG.products.subscribePremiumYearly?.price ? parseFloat(CONFIG.products.subscribePremiumYearly.price) / 100 : 359.99,
      type: "subscription",
      tier: 2,
      frequency: "Yearly",
    },
    subscribeLuxQuarterly: {
      name: CONFIG.products.subscribeLuxQuarterly?.title || "Lux Bark Box (Quarterly)",
      price: CONFIG.products.subscribeLuxQuarterly?.price ? parseFloat(CONFIG.products.subscribeLuxQuarterly.price) / 100 : 119.99,
      type: "subscription",
      tier: 3,
      frequency: "Quarterly",
    },
    subscribeLuxYearly: {
      name: CONFIG.products.subscribeLuxYearly?.title || "Lux Bark Box (Yearly)",
      price: CONFIG.products.subscribeLuxYearly?.price ? parseFloat(CONFIG.products.subscribeLuxYearly.price) / 100 : 439.99,
      type: "subscription",
      tier: 3,
      frequency: "Yearly",
    },
    singleBoxTrial: {
      name: CONFIG.products.singleBoxTrial?.title || "Trial Single Box",
      price: CONFIG.products.singleBoxTrial?.price ? parseFloat(CONFIG.products.singleBoxTrial.price) / 100 : 59.99,
      type: "one-time",
      tier: 0,
    },
    singleBoxBasic: {
      name: CONFIG.products.singleBoxBasic?.title || "Basic Single Box",
      price: CONFIG.products.singleBoxBasic?.price ? parseFloat(CONFIG.products.singleBoxBasic.price) / 100 : 79.99,
      type: "one-time",
      tier: 1,
    },
    singleBoxPremium: {
      name: CONFIG.products.singleBoxPremium?.title || "Premium Single Box",
      price: CONFIG.products.singleBoxPremium?.price ? parseFloat(CONFIG.products.singleBoxPremium.price) / 100 : 99.99,
      type: "one-time",
      tier: 2,
    },
    singleBoxLux: {
      name: CONFIG.products.singleBoxLux?.title || "Lux Single Box",
      price: CONFIG.products.singleBoxLux?.price ? parseFloat(CONFIG.products.singleBoxLux.price) / 100 : 119.99,
      type: "one-time",
      tier: 3,
    },
    luxAddon: {
      name: CONFIG.products.luxAddon?.title || "Lux Add-on Item",
      price: CONFIG.products.luxAddon?.price ? parseFloat(CONFIG.products.luxAddon.price) / 100 : 29.99,
      type: "addon",
    },
  },

  sizes: ["XS", "Small", "Medium", "Large", "XL"],
  genders: ["Male", "Female"],
};

// Debug logging - verify configuration loaded correctly
console.log('🔧 Wizard Configuration Loaded:');
console.log('CONFIG:', CONFIG);
console.log('PRODUCT_CONFIG.variantIds:', PRODUCT_CONFIG.variantIds);
console.log('PRODUCT_CONFIG.sellingPlans:', PRODUCT_CONFIG.sellingPlans);
console.log('PRODUCT_CONFIG.products:', PRODUCT_CONFIG.products);

// ========================================
// STATE MANAGEMENT
// ========================================

class WizardState {
  constructor() {
    this.currentStep = 1;
    this.data = this.getInitialData();
    this.eventListeners = new Map();
  }

  getInitialData() {
    return {
      dog: {
        name: "",
        gender: "",
        birthday: "",
        size: "",
      },
      box: {
        type: "",
        tier: "",
        productKey: "",
        frequency: "Quarterly", // Default to Quarterly
      },
      addons: {
        luxItem: false,
      },
      contact: {
        email: "",
      },
      selectedProducts: [],
    };
  }

  getCurrentStep() {
    return this.currentStep;
  }

  setCurrentStep(step) {
    if (step >= 1 && step <= CONSTANTS.TOTAL_STEPS) {
      this.currentStep = step;
    }
  }

  getData() {
    return this.data;
  }

  updateData(path, value) {
    const keys = path.split(".");
    let obj = this.data;

    for (let i = 0; i < keys.length - 1; i++) {
      obj = obj[keys[i]];
    }

    obj[keys[keys.length - 1]] = value;
  }

  // Clean up event listeners to prevent memory leaks
  addListener(key, element, event, handler) {
    if (!this.eventListeners.has(key)) {
      this.eventListeners.set(key, []);
    }
    element.addEventListener(event, handler);
    this.eventListeners.get(key).push({ element, event, handler });
  }

  removeListeners(key) {
    if (this.eventListeners.has(key)) {
      this.eventListeners.get(key).forEach(({ element, event, handler }) => {
        element.removeEventListener(event, handler);
      });
      this.eventListeners.delete(key);
    }
  }
}

// ========================================
// UTILITY FUNCTIONS
// ========================================

const Utils = {
  $: (selector, root = document) => root.querySelector(selector),

  $$: (selector, root = document) =>
    Array.from(root.querySelectorAll(selector)),

  formatCurrency: (amount) => `$${amount.toFixed(2)}`,

  validateEmail: (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email),

  debounce: (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  },
};

// ========================================
// STORAGE MANAGER
// ========================================

const StorageManager = {
  save: (state) => {
    try {
      localStorage.setItem(
        CONSTANTS.STORAGE_KEYS.DATA,
        JSON.stringify(state.getData())
      );
      localStorage.setItem(
        CONSTANTS.STORAGE_KEYS.STEP,
        String(state.getCurrentStep())
      );
      return true;
    } catch (error) {
      console.error("Failed to save to localStorage:", error);
      return false;
    }
  },

  load: (state) => {
    try {
      // Only load data, not the step - always start from step 1
      const savedData = localStorage.getItem(CONSTANTS.STORAGE_KEYS.DATA);

      if (savedData) {
        const parsedData = JSON.parse(savedData);
        state.data = { ...state.getInitialData(), ...parsedData };
      }

      // Always reset to step 1 on page load
      state.setCurrentStep(1);

      return true;
    } catch (error) {
      console.error("Failed to load from localStorage:", error);
      return false;
    }
  },

  clear: () => {
    try {
      localStorage.removeItem(CONSTANTS.STORAGE_KEYS.DATA);
      localStorage.removeItem(CONSTANTS.STORAGE_KEYS.STEP);
    } catch (error) {
      console.error("Failed to clear localStorage:", error);
    }
  },
};

// ========================================
// PRODUCT MANAGER
// ========================================

const ProductManager = {
  updateSelectedProducts: (wizardData) => {
    wizardData.selectedProducts = [];

    console.log('📦 Updating Selected Products...');
    console.log('Product Key:', wizardData.box.productKey);
    console.log('Dog Size:', wizardData.dog.size);

    // Add main box product
    if (wizardData.box.productKey && wizardData.dog.size) {
      const product = PRODUCT_CONFIG.products[wizardData.box.productKey];
      const variantConfig =
        PRODUCT_CONFIG.variantIds[wizardData.box.productKey];

      console.log('Product Config:', product);
      console.log('Variant Config:', variantConfig);

      // Get the correct variant ID based on dog size
      const variantId =
        typeof variantConfig === "object"
          ? variantConfig[wizardData.dog.size]
          : variantConfig;

      console.log('Selected Variant ID:', variantId);

      // Get selling plan ID if it's a subscription product
      const sellingPlanId =
        product.type === "subscription"
          ? PRODUCT_CONFIG.sellingPlans[wizardData.box.productKey]
          : null;

      console.log('Selling Plan ID:', sellingPlanId);

      if (product && variantId) {
        wizardData.selectedProducts.push({
          variantId,
          productKey: wizardData.box.productKey,
          name: `${product.name} (${wizardData.dog.size})`,
          price: product.price,
          type: product.type,
          size: wizardData.dog.size,
          sellingPlanId,
          quantity: 1,
        });
        console.log('✅ Product added to cart');
      } else {
        console.error('❌ Failed to add product - missing variant ID or product config');
      }
    }

    // Add lux addon if selected
    if (wizardData.addons.luxItem) {
      const product = PRODUCT_CONFIG.products.luxAddon;
      const variantId = PRODUCT_CONFIG.variantIds.luxAddon;

      if (product && variantId) {
        wizardData.selectedProducts.push({
          variantId,
          productKey: "luxAddon",
          name: product.name,
          price: product.price,
          type: product.type,
          sellingPlanId: null,
          quantity: 1,
        });
      }
    }
  },

  getProductKeyFromTier: (tier, frequency) => {
    // Map tier and frequency to the correct product key
    const tierFrequencyMap = {
      [CONSTANTS.TIERS.TRIAL]: {
        "Quarterly": "subscribeTrialQuarterly",
        "Yearly": "subscribeTrialYearly",
        "One-Time": "singleBoxTrial",
      },
      [CONSTANTS.TIERS.BASIC]: {
        "Quarterly": "subscribeBasicQuarterly",
        "Yearly": "subscribeBasicYearly",
        "One-Time": "singleBoxBasic",
      },
      [CONSTANTS.TIERS.PREMIUM]: {
        "Quarterly": "subscribePremiumQuarterly",
        "Yearly": "subscribePremiumYearly",
        "One-Time": "singleBoxPremium",
      },
      [CONSTANTS.TIERS.LUX]: {
        "Quarterly": "subscribeLuxQuarterly",
        "Yearly": "subscribeLuxYearly",
        "One-Time": "singleBoxLux",
      },
    };
    
    // Default to Quarterly if frequency not specified
    const freq = frequency || "Quarterly";
    return tierFrequencyMap[tier]?.[freq] || null;
  },

  calculateTotal: (products) => {
    return products.reduce((sum, p) => sum + p.price * p.quantity, 0);
  },
};

// ========================================
// UI MANAGER
// ========================================

const UIManager = {
  updateProgress: (currentStep) => {
    const percent = Math.max(
      0,
      Math.min(100, (currentStep / CONSTANTS.TOTAL_STEPS) * 100)
    );
    const bar = Utils.$("#progress-bar");
    if (bar) {
      bar.style.width = `${percent}%`;
      bar.setAttribute("aria-valuenow", percent);
    }
  },

  showStep: (stepNumber, state) => {
    // Hide all steps
    Utils.$$(".step").forEach((s) => s.classList.remove("active"));

    // Show target step
    const targetStep = Utils.$(`.step[data-step="${stepNumber}"]`);
    if (!targetStep) return false;

    targetStep.classList.add("active");
    state.setCurrentStep(stepNumber);

    // Update dynamic content
    UIManager.updateDogNameDisplay(state.getData().dog.name);
    UIManager.updateProgress(stepNumber);
    
    // Render review on step 8
    if (stepNumber === 8) {
      UIManager.renderReview(state.getData());
    }

    // Save state (data only, not step)
    StorageManager.save(state);

    return true;
  },

  updateDogNameDisplay: (name) => {
    const nameSpan = Utils.$("#dogNameDisplay");
    if (nameSpan) {
      nameSpan.textContent = name || "your dog";
    }
  },

  toggleConditionalSections: () => {
    const subscribeRadio = Utils.$('input[name="boxType"][value="Subscribe"]');
    const singleRadio = Utils.$('input[name="boxType"][value="Single"]');
    const tiersSection = Utils.$("#subscriptionTiers");
    const singleTiersSection = Utils.$("#singleBoxTiers");
    const frequencySection = Utils.$("#subscriptionFrequency");

    if (!subscribeRadio || !singleRadio || !tiersSection || !singleTiersSection) {
      return;
    }

    if (subscribeRadio.checked) {
      tiersSection.hidden = false;
      singleTiersSection.hidden = true;
      if (frequencySection) frequencySection.hidden = false;
    } else if (singleRadio.checked) {
      tiersSection.hidden = true;
      singleTiersSection.hidden = false;
      if (frequencySection) frequencySection.hidden = true;
    } else {
      tiersSection.hidden = true;
      singleTiersSection.hidden = true;
      if (frequencySection) frequencySection.hidden = true;
    }
  },

  updatePriceDisplay: (frequency) => {
    const tierPrices = Utils.$$(".tier-price");
    
    tierPrices.forEach((priceEl) => {
      const quarterlyPrice = priceEl.getAttribute("data-quarterly");
      const yearlyPrice = priceEl.getAttribute("data-yearly");
      
      if (frequency === "Yearly" && yearlyPrice) {
        priceEl.textContent = `$${yearlyPrice}`;
      } else if (quarterlyPrice) {
        priceEl.textContent = `$${quarterlyPrice}`;
      }
    });
  },

  toggleBirthdayInput: () => {
    const notSureCheckbox = Utils.$("#birthdayNotSure");
    const birthdayInput = Utils.$("#dogBirthday");

    if (!notSureCheckbox || !birthdayInput) return;

    if (notSureCheckbox.checked) {
      birthdayInput.disabled = true;
      birthdayInput.value = "";
      birthdayInput.style.opacity = "0.5";
    } else {
      birthdayInput.disabled = false;
      birthdayInput.style.opacity = "1";
    }
  },

  renderReview: (wizardData) => {
    const review = Utils.$("#review");
    if (!review) return;

    const total = ProductManager.calculateTotal(wizardData.selectedProducts);

    // Log all wizard data before checkout
    console.log('=== WIZARD REVIEW - BEFORE CHECKOUT ===' );
    console.log('Dog Info:', wizardData.dog);
    console.log('Box Selection:', wizardData.box);
    console.log('Add-ons:', wizardData.addons);
    console.log('Contact:', wizardData.contact);
    console.log('Selected Products:', wizardData.selectedProducts);
    console.log('Total Amount:', total);
    console.log('========================================');

    review.innerHTML = `
      <div class="review-section">
        <h3>Dog Information</h3>
        <dl>
          <dt>Name:</dt>
          <dd>${wizardData.dog.name || "-"}</dd>
          <dt>Gender:</dt>
          <dd>${wizardData.dog.gender || "-"}</dd>
          <dt>Birthday:</dt>
          <dd>${wizardData.dog.birthday || "-"}</dd>
          <dt>Size:</dt>
          <dd>${wizardData.dog.size || "-"}</dd>
        </dl>
      </div>

      <div class="review-section">
        <h3>Box Selection</h3>
        <dl>
          <dt>Type:</dt>
          <dd>${wizardData.box.type || "-"}</dd>
          ${
            wizardData.box.type === CONSTANTS.BOX_TYPES.SUBSCRIBE
              ? `<dt>Tier:</dt><dd>${wizardData.box.tier || "-"}</dd>
                 <dt>Frequency:</dt><dd>${wizardData.box.frequency || "-"}</dd>`
              : ""
          }
          <dt>Lux Add-on:</dt>
          <dd>${wizardData.addons.luxItem ? "Yes" : "No"}</dd>
        </dl>
      </div>

      <div class="review-section">
        <h3>Selected Products</h3>
        <ul>
          ${wizardData.selectedProducts
            .map((p) => `<li>${p.name} - ${Utils.formatCurrency(p.price)}</li>`)
            .join("")}
        </ul>
        <p class="total"><strong>Total: ${Utils.formatCurrency(
          total
        )}</strong></p>
      </div>

      <div class="review-section">
        <h3>Contact Information</h3>
        <dl>
          <dt>Email:</dt>
          <dd>${wizardData.contact.email || "-"}</dd>
        </dl>
      </div>
    `;
  },
};

// ========================================
// DATA MANAGER
// ========================================

const DataManager = {
  saveStepData: (stepNumber, state) => {
    const wizardData = state.getData();

    switch (stepNumber) {
      case 1:
        wizardData.dog.name = (Utils.$("#dogName")?.value || "").trim();
        break;

      case 2: {
        const gender = Utils.$('input[name="dogGender"]:checked');
        wizardData.dog.gender = gender ? gender.value : "";
        break;
      }

      case 3: {
        const notSureCheckbox = Utils.$("#birthdayNotSure");
        if (notSureCheckbox && notSureCheckbox.checked) {
          wizardData.dog.birthday = "Not sure";
        } else {
          wizardData.dog.birthday = (Utils.$("#dogBirthday")?.value || "").trim();
        }
        break;
      }

      case 4: {
        const size = Utils.$('input[name="dogSize"]:checked');
        wizardData.dog.size = size ? size.value : "";
        break;
      }

      case 5: {
        const boxType = Utils.$('input[name="boxType"]:checked');
        wizardData.box.type = boxType ? boxType.value : "";

        if (wizardData.box.type === CONSTANTS.BOX_TYPES.SUBSCRIBE) {
          const tier = Utils.$('input[name="tier"]:checked');
          const frequency = Utils.$('input[name="subscriptionFrequency"]:checked');
          
          wizardData.box.tier = tier ? tier.value : "";
          wizardData.box.frequency = frequency ? frequency.value : "Quarterly";
          // Pass frequency to getProductKeyFromTier
          wizardData.box.productKey = ProductManager.getProductKeyFromTier(
            wizardData.box.tier,
            wizardData.box.frequency
          );
        } else if (wizardData.box.type === CONSTANTS.BOX_TYPES.SINGLE) {
          const singleTier = Utils.$('input[name="singleTier"]:checked');
          wizardData.box.tier = singleTier ? singleTier.value : "";
          wizardData.box.frequency = "One-Time";
          // Use getProductKeyFromTier with "One-Time" frequency
          wizardData.box.productKey = ProductManager.getProductKeyFromTier(
            wizardData.box.tier,
            wizardData.box.frequency
          );
        }

        ProductManager.updateSelectedProducts(wizardData);
        break;
      }

      case 6: {
        const addon = Utils.$('input[name="luxAddon"]:checked');
        wizardData.addons.luxItem = addon ? addon.value === "Yes" : false;
        ProductManager.updateSelectedProducts(wizardData);
        break;
      }

      case 7:
        wizardData.contact.email = (Utils.$("#userEmail")?.value || "").trim();
        break;

      case 8:
        UIManager.renderReview(wizardData);
        break;
    }

    StorageManager.save(state);
  },

  restoreFormInputs: (wizardData) => {
    // Dog name
    const nameEl = Utils.$("#dogName");
    if (nameEl) nameEl.value = wizardData.dog.name || "";

    // Dog gender
    if (wizardData.dog.gender) {
      const gender = Utils.$(
        `input[name="dogGender"][value="${wizardData.dog.gender}"]`
      );
      if (gender) gender.checked = true;
    }

    // Dog birthday
    const birthdayEl = Utils.$("#dogBirthday");
    const notSureCheckbox = Utils.$("#birthdayNotSure");
    if (wizardData.dog.birthday === "Not sure") {
      if (notSureCheckbox) notSureCheckbox.checked = true;
      if (birthdayEl) birthdayEl.value = "";
    } else {
      if (birthdayEl) birthdayEl.value = wizardData.dog.birthday || "";
      if (notSureCheckbox) notSureCheckbox.checked = false;
    }

    // Dog size
    if (wizardData.dog.size) {
      const size = Utils.$(
        `input[name="dogSize"][value="${wizardData.dog.size}"]`
      );
      if (size) size.checked = true;
    }

    // Box type
    if (wizardData.box.type) {
      const boxType = Utils.$(
        `input[name="boxType"][value="${wizardData.box.type}"]`
      );
      if (boxType) boxType.checked = true;
    }

    // Tier (for subscriptions)
    if (
      wizardData.box.tier &&
      wizardData.box.type === CONSTANTS.BOX_TYPES.SUBSCRIBE
    ) {
      const tier = Utils.$(
        `input[name="tier"][value="${wizardData.box.tier}"]`
      );
      if (tier) tier.checked = true;
    }

    // Single box tier
    if (
      wizardData.box.tier &&
      wizardData.box.type === CONSTANTS.BOX_TYPES.SINGLE
    ) {
      const singleTier = Utils.$(
        `input[name="singleTier"][value="${wizardData.box.tier}"]`
      );
      if (singleTier) singleTier.checked = true;
    }

    // Subscription frequency
    if (wizardData.box.frequency) {
      const frequency = Utils.$(
        `input[name="subscriptionFrequency"][value="${wizardData.box.frequency}"]`
      );
      if (frequency) {
        frequency.checked = true;
        UIManager.updatePriceDisplay(wizardData.box.frequency);
      }
    }

    // Lux addon
    if (wizardData.addons.luxItem !== undefined) {
      const luxValue = wizardData.addons.luxItem ? "Yes" : "No";
      const addon = Utils.$(`input[name="luxAddon"][value="${luxValue}"]`);
      if (addon) addon.checked = true;
    }

    // Email
    const emailEl = Utils.$("#userEmail");
    if (emailEl) emailEl.value = wizardData.contact.email || "";
  },
};

// ========================================
// VALIDATION MANAGER
// ========================================

const ValidationManager = {
  validators: {
    1: (stepEl) => {
      const input = Utils.$("#dogName", stepEl);
      return () => {
        const isValid = !!(input && input.value.trim());
        console.log('Step 1 validation:', { hasInput: !!input, value: input?.value, isValid });
        return isValid;
      };
    },

    2: (stepEl) => {
      return () => !!Utils.$('input[name="dogGender"]:checked', stepEl);
    },

    3: (stepEl) => {
      const input = Utils.$("#dogBirthday", stepEl);
      const notSureCheckbox = Utils.$("#birthdayNotSure", stepEl);
      // Valid if either has a date OR 'not sure' is checked
      return () => {
        if (notSureCheckbox && notSureCheckbox.checked) return true;
        return !!(input && input.value.trim());
      };
    },

    4: (stepEl) => {
      return () => !!Utils.$('input[name="dogSize"]:checked', stepEl);
    },

    5: (stepEl) => {
      return () => {
        const boxChoice = Utils.$('input[name="boxType"]:checked', stepEl);
        if (!boxChoice) return false;

        if (boxChoice.value === CONSTANTS.BOX_TYPES.SUBSCRIBE) {
          return !!Utils.$('input[name="tier"]:checked', stepEl);
        } else if (boxChoice.value === CONSTANTS.BOX_TYPES.SINGLE) {
          return !!Utils.$('input[name="singleTier"]:checked', stepEl);
        }

        return false;
      };
    },

    6: (stepEl) => {
      return () => !!Utils.$('input[name="luxAddon"]:checked', stepEl);
    },

    7: (stepEl) => {
      const input = Utils.$("#userEmail", stepEl);
      return () => {
        if (!input || !input.value.trim()) return false;
        return Utils.validateEmail(input.value.trim());
      };
    },

    8: () => {
      return () => true; // Review step is always valid
    },
  },

  setupValidation: (stepNumber, state) => {
    const stepEl = Utils.$(`.step[data-step="${stepNumber}"]`);
    if (!stepEl) return;

    const nextBtn = Utils.$(".next-btn", stepEl);
    if (!nextBtn) return;

    // Clean up previous listeners
    state.removeListeners(`validation-step-${stepNumber}`);

    // Get validator for current step
    const validator = ValidationManager.validators[stepNumber];
    if (!validator) {
      nextBtn.disabled = false;
      return;
    }

    const validate = validator(stepEl);
    const updateButton = () => {
      nextBtn.disabled = !validate();
    };

    // Add appropriate event listeners based on step
    const listenersConfig = {
      1: [{ selector: "#dogName", event: "input" }],
      2: [
        {
          selector: 'input[name="dogGender"]',
          event: "change",
          multiple: true,
        },
      ],
      3: [
        { selector: "#dogBirthday", event: "input" },
        { 
          selector: "#birthdayNotSure", 
          event: "change",
          callback: () => UIManager.toggleBirthdayInput()
        }
      ],
      4: [
        { selector: 'input[name="dogSize"]', event: "change", multiple: true },
      ],
      5: [
        {
          selector: 'input[name="boxType"]',
          event: "change",
          multiple: true,
          callback: () => UIManager.toggleConditionalSections(),
        },
        { selector: 'input[name="tier"]', event: "change", multiple: true },
        { selector: 'input[name="singleTier"]', event: "change", multiple: true },
        { 
          selector: 'input[name="subscriptionFrequency"]', 
          event: "change", 
          multiple: true,
          callback: (e) => {
            const frequency = e.target.value;
            UIManager.updatePriceDisplay(frequency);
          }
        },
      ],
      6: [
        { selector: 'input[name="luxAddon"]', event: "change", multiple: true },
      ],
      7: [{ selector: "#userEmail", event: "input" }],
    };

    const config = listenersConfig[stepNumber];
    if (config) {
      config.forEach(({ selector, event, multiple, callback }) => {
        const elements = multiple
          ? Utils.$$(selector, stepEl)
          : [Utils.$(selector, stepEl)];

        elements.forEach((el) => {
          if (el) {
            const handler = (e) => {
              if (callback) callback(e);
              updateButton();
            };
            state.addListener(
              `validation-step-${stepNumber}`,
              el,
              event,
              handler
            );
          }
        });
      });
    }

    // Initial validation
    if (stepNumber === 5) UIManager.toggleConditionalSections();
    updateButton();
  },
};

// ========================================
// NAVIGATION MANAGER
// ========================================

const NavigationManager = {
  goToStep: (stepNumber, state) => {
    if (UIManager.showStep(stepNumber, state)) {
      ValidationManager.setupValidation(stepNumber, state);
    }
  },

  handleNext: (state) => {
    const currentStep = state.getCurrentStep();
    DataManager.saveStepData(currentStep, state);

    // Navigate to next step
    const nextStep = currentStep + 1;
    if (nextStep <= CONSTANTS.TOTAL_STEPS) {
      NavigationManager.goToStep(nextStep, state);
    }
  },

  handlePrev: (state) => {
    const currentStep = state.getCurrentStep();
    const prevStep = Math.max(1, currentStep - 1);
    NavigationManager.goToStep(prevStep, state);
  },
};

// ========================================
// CHECKOUT MANAGER
// ========================================

const CheckoutManager = {
  buildCartItem: (product, properties) => {
    const item = {
      id: parseInt(product.variantId, 10), // Ensure numeric variant ID
      quantity: product.quantity,
      properties:
        product.type === "addon" ? { "Added From Wizard": "Yes" } : properties,
    };

    // Add selling_plan for subscription products (must be an integer)
    if (product.sellingPlanId) {
      const planId = parseInt(product.sellingPlanId, 10);

      // Validate that selling plan ID is a valid number
      if (isNaN(planId)) {
        console.error(
          `Invalid selling plan ID: "${product.sellingPlanId}" for product ${product.name}.`,
          "Please ensure products have valid selling plans configured in Shopify."
        );
        throw new Error(
          `Invalid selling plan configuration for ${product.name}.`
        );
      }

      item.selling_plan = planId;
    }

    return item;
  },

  buildProperties: (wizardData) => ({
    "Dog Name": wizardData.dog.name,
    Gender: wizardData.dog.gender,
    Birthday: wizardData.dog.birthday,
    Size: wizardData.dog.size,
    "Box Type": wizardData.box.type,
    Tier: wizardData.box.tier || "",
    Frequency: wizardData.box.frequency || "",
    "Lux Add-on": wizardData.addons.luxItem ? "Yes" : "No",
    Email: wizardData.contact.email,
  }),

  addToCart: async (item) => {
    console.log('🛒 Adding to cart:', item);
    try {
      const response = await fetch(CONSTANTS.CART_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(item),
      });

      console.log('Cart API Response Status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Cart API Error Response:', errorText);
        throw new Error(`Cart API error (${response.status}): ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Cart API Success:', result);

      return { success: true, data: result };
    } catch (error) {
      console.error("❌ Failed to add item to cart:", error);
      return { success: false, error };
    }
  },

  proceedToCheckout: async (wizardData) => {
    console.log('🚀 Starting Checkout Process...');
    console.log('Wizard Data:', wizardData);

    if (!wizardData.selectedProducts || wizardData.selectedProducts.length === 0) {
      console.error('❌ No products selected!');
      alert('No products selected. Please complete the wizard.');
      return false;
    }

    // Clear the cart first to ensure a clean checkout
    console.log('🧹 Clearing cart...');
    try {
      await fetch('/cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      console.log('✅ Cart cleared successfully');
    } catch (error) {
      console.error('⚠️ Failed to clear cart:', error);
      // Continue anyway - cart might already be empty
    }

    const properties = CheckoutManager.buildProperties(wizardData);
    console.log('Properties:', properties);

    const items = wizardData.selectedProducts.map((product) =>
      CheckoutManager.buildCartItem(product, properties)
    );

    console.log('Cart Items to Add:', items);

    // Add items to cart sequentially
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      console.log(`Adding item ${i + 1}/${items.length}:`, item);
      
      const result = await CheckoutManager.addToCart(item);
      
      if (!result.success) {
        console.error('❌ Failed to add item:', item, 'Error:', result.error);
        alert(`Failed to add ${wizardData.selectedProducts[i].name} to cart. Error: ${result.error?.message || 'Unknown error'}`);
        return false;
      }
      
      console.log(`✅ Item ${i + 1}/${items.length} added successfully`);
    }

    console.log('✅ All items added to cart successfully!');
    console.log('Redirecting to:', CONSTANTS.CHECKOUT_URL);

    // Navigate to checkout
    window.location.href = CONSTANTS.CHECKOUT_URL;
    return true;
  },
};

// ========================================
// WIZARD CONTROLLER
// ========================================

class WizardController {
  constructor() {
    this.state = new WizardState();
    this.initialized = false;
  }

  init() {
    if (this.initialized) return;

    // Initialize progress bar
    const bar = Utils.$("#progress-bar");
    if (bar) {
      bar.setAttribute("aria-valuemin", "0");
      bar.setAttribute("aria-valuemax", "100");
    }

    // Clear any hash from URL
    if (window.location.hash) {
      history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search
      );
    }

    // Load saved data (but always start at step 1)
    StorageManager.load(this.state);

    // Restore form inputs
    DataManager.restoreFormInputs(this.state.getData());

    // Always show step 1 on page load
    NavigationManager.goToStep(1, this.state);

    // Bind event listeners using event delegation
    this.bindEvents();

    this.initialized = true;
  }

  bindEvents() {
    // Use event delegation for better performance
    const wizard = Utils.$("#bark-style-wizard");
    if (!wizard) return;

    wizard.addEventListener("click", (e) => {
      const target = e.target;
      const action = target.getAttribute("data-action");

      if (!action) return;

      e.preventDefault();

      switch (action) {
        case "next":
          NavigationManager.handleNext(this.state);
          break;
        case "prev":
          NavigationManager.handlePrev(this.state);
          break;
        case "checkout":
          CheckoutManager.proceedToCheckout(this.state.getData());
          break;
      }
    });

    // Handle page unload (save data only)
    window.addEventListener("beforeunload", () => {
      StorageManager.save(this.state);
    });
  }

  // Public method to reset wizard
  reset() {
    this.state = new WizardState();
    StorageManager.clear();
    DataManager.restoreFormInputs(this.state.getData());
    NavigationManager.goToStep(1, this.state);
  }
}

// ========================================
// INITIALIZATION
// ========================================

// Initialize wizard when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  const wizard = new WizardController();
  wizard.init();

  // Expose wizard to window for debugging (remove in production)
  if (process?.env?.NODE_ENV === "development") {
    window.wizard = wizard;
  }
});
 